import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import PodcastTerms from "@/pages/PodcastTerms";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { userCan } from "@/lib/rbac";
import { CmsProvider } from "@/lib/cms";
import MarketingLayout from "@/layouts/MarketingLayout";
import DashboardLayout from "@/layouts/DashboardLayout";
import Home from "@/pages/Home";
import Programs from "@/pages/Programs";
import ProgramDetails from "@/pages/ProgramDetails";
import Gallery from "@/pages/Gallery";
import AlbumView from "@/pages/AlbumView";
import Podcast from "@/pages/Podcast";
import PodcastArticle from "@/pages/PodcastArticle";
import RegisterTraining from "@/pages/RegisterTraining";
import Book from "@/pages/Book";
import Verify from "@/pages/Verify";
import Experts from "@/pages/Experts";
import ExpertApply from "@/pages/ExpertApply";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Faq from "@/pages/Faq";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import AdminOverview from "@/pages/admin/Overview";
import AdminSchools from "@/pages/admin/Schools";
import AdminTeachers from "@/pages/admin/Teachers";
import AdminBookings from "@/pages/admin/Bookings";
import AdminPrograms from "@/pages/admin/Programs";
import AdminProgramEdit from "@/pages/admin/ProgramEdit";
import AdminHeaderBuilder from "@/pages/admin/HeaderBuilder";
import AdminGallery from "@/pages/admin/Gallery";
import AdminCertificates from "@/pages/admin/Certificates";
import AdminCertificateDesigner from "@/pages/admin/CertificateDesigner";
import AdminSettings from "@/pages/admin/Settings";
import AdminEmailIntegration from "@/pages/admin/EmailIntegration";
import AdminWebsiteIdentity from "@/pages/admin/WebsiteIdentity";
import AdminWebsiteMetadata from "@/pages/admin/WebsiteIdentity"; // same page, dedicated route
import AdminNewsTickerAdmin from "@/pages/admin/NewsTickerAdmin";
import AdminAchievements from "@/pages/admin/AdminAchievements";
import Achievements from "@/pages/Achievements";
import AdminUserManagement from "@/pages/admin/UserManagement";
import AdminAuditLog from "@/pages/admin/AuditLog";
import AdminLegalCms from "@/pages/admin/LegalCms";
import AdminMyProfile from "@/pages/admin/MyProfile";
import AdminContent from "@/pages/admin/Content";
import AdminFooterCms from "@/pages/admin/FooterCms";
import AdminProspectusWidget from "@/pages/admin/ProspectusWidget";
import AdminVisitorAnalytics from "@/pages/admin/VisitorAnalytics";
import AdminVisitorCounterWidget from "@/pages/admin/VisitorCounterWidget";
import AdminPodcast from "@/pages/admin/Podcast";
import AdminPodcastBookings from "@/pages/admin/PodcastBookings";
import AdminExpertApplications from "@/pages/admin/ExpertApplications";
import { AdminRegistrationsList, AdminRegistrationDetail } from "@/pages/admin/Registrations";
import AdminReports from "@/pages/admin/Reports";
import AdminCalendar from "@/pages/admin/CalendarView";
import { AdminFeedbackList, AdminFeedbackDetail } from "@/pages/admin/Feedback";
import Feedback from "@/pages/Feedback";
import PortalHome from "@/pages/portal/Home";

function Protected({ children, role, permission }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (permission && !userCan(user, permission)) return <Navigate to="/" replace />;
  if (role === "admin" && !userCan(user, "dashboard:view")) return <Navigate to="/" replace />;
  return children;
}

// Single global scroll-to-top: fires on every route change.
// Hash links (#section) are excluded so anchor navigation still works.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <CmsProvider>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/programs" element={<Programs />} />
            <Route path="/programs/:id" element={<ProgramDetails />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/gallery/:id" element={<AlbumView />} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/podcast" element={<Podcast />} />
            <Route path="/podcast/:slug" element={<PodcastArticle />} />
            <Route path="/book" element={<Book />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/verify/:certId" element={<Verify />} />
            <Route path="/experts" element={<Experts />} />
            <Route path="/experts/apply" element={<ExpertApply />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<Faq />} />
          </Route>
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/feedback/:programId" element={<Feedback />} />
          <Route path="/register-training/:token" element={<RegisterTraining />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/terms"         element={<Terms />} />
          <Route path="/privacy"       element={<Privacy />} />
          <Route path="/podcast-terms" element={<PodcastTerms />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/admin" element={<Protected role="admin"><DashboardLayout /></Protected>}>
            <Route index element={<AdminOverview />} />
            <Route path="schools" element={<Protected permission="schools:read"><AdminSchools /></Protected>} />
            <Route path="teachers" element={<Protected permission="teachers:read"><AdminTeachers /></Protected>} />
            <Route path="bookings" element={<Protected permission="bookings:read"><AdminBookings /></Protected>} />
            <Route path="programs" element={<Protected permission="programs:read"><AdminPrograms /></Protected>} />
            <Route path="programs/:id" element={<Protected permission="programs:read"><AdminProgramEdit /></Protected>} />
            <Route path="gallery" element={<Protected permission="gallery:read"><AdminGallery /></Protected>} />
            <Route path="certificates" element={<Protected permission="certificates:read"><AdminCertificates /></Protected>} />
            <Route path="certificate-designer" element={<Protected permission="certificates:write"><AdminCertificateDesigner /></Protected>} />
            <Route path="settings" element={<Protected permission="settings:manage"><AdminSettings /></Protected>} />
            <Route path="email-integration" element={<Protected permission="settings:manage"><AdminEmailIntegration /></Protected>} />
            <Route path="website-identity" element={<Protected permission="settings:manage"><AdminWebsiteIdentity /></Protected>} />
            <Route path="website-metadata" element={<Protected permission="settings:manage"><AdminWebsiteMetadata /></Protected>} />
            <Route path="news-ticker" element={<Protected permission="cms:read"><AdminNewsTickerAdmin /></Protected>} />
            <Route path="achievements" element={<Protected permission="cms:read"><AdminAchievements /></Protected>} />
            <Route path="content" element={<Protected permission="cms:read"><AdminContent /></Protected>} />
            <Route path="footer" element={<Protected permission="cms:read"><AdminFooterCms /></Protected>} />
            <Route path="prospectus-widget" element={<Protected permission="cms:read"><AdminProspectusWidget /></Protected>} />
            <Route path="visitor-counter-widget" element={<Protected permission="cms:read"><AdminVisitorCounterWidget /></Protected>} />
            <Route path="visitor-analytics" element={<Protected permission="reports:view"><AdminVisitorAnalytics /></Protected>} />
            <Route path="header-builder" element={<Protected permission="cms:read"><AdminHeaderBuilder /></Protected>} />
            <Route path="users"      element={<Protected permission="users:read"><AdminUserManagement /></Protected>} />
            <Route path="audit-log" element={<Protected permission="users:read"><AdminAuditLog /></Protected>} />
            <Route path="profile"   element={<Protected permission="dashboard:view"><AdminMyProfile /></Protected>} />
            <Route path="legal"    element={<Protected permission="cms:read"><AdminLegalCms /></Protected>} />
            <Route path="podcast" element={<Protected permission="podcast:read"><AdminPodcast /></Protected>} />
            <Route path="podcast-bookings" element={<Protected permission="podcast:read"><AdminPodcastBookings /></Protected>} />
            <Route path="experts" element={<Protected permission="experts:read"><AdminExpertApplications /></Protected>} />
            <Route path="registrations" element={<Protected permission="bookings:read"><AdminRegistrationsList /></Protected>} />
            <Route path="registrations/:id" element={<Protected permission="bookings:read"><AdminRegistrationDetail /></Protected>} />
            <Route path="reports" element={<Protected permission="reports:view"><AdminReports /></Protected>} />
            <Route path="calendar" element={<Protected permission="bookings:read"><AdminCalendar /></Protected>} />
            <Route path="feedback" element={<Protected permission="certificates:read"><AdminFeedbackList /></Protected>} />
            <Route path="feedback/:id" element={<Protected permission="certificates:read"><AdminFeedbackDetail /></Protected>} />
          </Route>
          <Route path="/portal" element={<Protected><PortalHome /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </CmsProvider>
    </AuthProvider>
  );
}

export default App;
